import React, { useEffect, useState } from 'react'
import { Header, LayoutContainer, NavBar, Pagination } from '../../components'
import { useTranslation } from 'react-i18next'
import {
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material'
import { TransporterModal } from '../../components'
import { useQuery } from '@apollo/client'
import { gqlTransporter } from '../../gql'

function Transporters() {
  const { t } = useTranslation('transporters')
  const [rowsPerPage, setRowsPerPage] = useState(20) // Nombre de lignes par pages
  const [first, setFirst] = useState(rowsPerPage)
  const [last, setLast] = useState(null)
  const [after, setAfter] = useState(null)
  const [before, setBefore] = useState(null)
  const { data, error, loading, refetch } = useQuery(gqlTransporter.TRANSPORTERS, {
    variables: { first, after, before, last },
  })
  const [transportersState, setTransportersState] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const blankTransporter = {
    id: '',
    name: '',
    color: '',
    transporterContacts: [],
  }
  const [currentTransporter, setCurrentTransporter] = useState(blankTransporter)
  const [pageInfo, setPageInfo] = useState(null)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)

  useEffect(() => {
    if (data) {
      const transformedTransporters = data.transporters.edges.map(({ node }) => ({
        id: node.id,
        nodeId: node.nodeId,
        name: node.name,
        color: node.color,
        email: node.email,
        firstNameContact: node.firstNameContact,
        lastNameContact: node.lastNameContact,
        phoneNumber: node.phoneNumber,
        transporterContacts: (node.transporterContacts?.nodes || []).map((contact) => ({
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phoneNumber: contact.phoneNumber,
          isPrimary: contact.isPrimary,
          isActive: contact.isActive,
        })),
        users: node.users.nodes.map((user) => ({
          id: user.id,
          firstname: user.firstname,
          lastname: user.lastname,
          licensePlate: user.licensePlate,
          isActive: user.isActive,
          companyRole: user.companyRole,
        })),
      }))
      setTransportersState(transformedTransporters)
      setPageInfo(data.transporters.pageInfo)
      setTotalCount(data.transporters.totalCount)
    }
  }, [data])

 
  const handleRefreshList = async () => {
    try {
      await refetch()
    } catch (error) {
      console.error('Error refreshing list:', error)
    }
  }

  const handleEditClick = (transporter) => {
    setModalMode('edit')
    setCurrentTransporter(transporter)
    setIsModalOpen(true)
  }

  const handleAddTransporter = () => {
    setModalMode('create')
    setCurrentTransporter(blankTransporter) // Envoir un transporter avec les infos vides
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / rowsPerPage) ?? 0

  // // Handle page change
  const handleChangePage = (newPage, type) => {
    if (type === 'next') {
      setAfter(pageInfo.endCursor) // Récupération des données suivantes à partir du dernier curseur connu
      setBefore(null)
      setFirst(rowsPerPage)
      setLast(null)
    } else if (type === 'previous') {
      setAfter(null)
      setBefore(pageInfo.startCursor) // Récupération des données précédentes à partir du dernier curseur connu
      setFirst(null)
      setLast(rowsPerPage)
    }
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage)
    setFirst(newRowsPerPage)
    setLast(null)
    setAfter(null)
    setBefore(null)
    setPage(0) // Reset to the first page
  }

  return (
    <div className="transporters">
      <Header title={t('title')} />
      <NavBar active="transporters" />
      <LayoutContainer>
        <Box sx={{ m: 1.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <Card>
                <CardContent>
                {loading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="300px"
                    >
                      <CircularProgress
                        size={80}
                        sx={{
                          svg: {
                            width: '100%',
                            height: '100%',
                            viewBox: '0 0 40 40', // Custom viewBox
                          },
                        }}
                      />
                    </Box>
                  ) : error ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="300px"
                    >
                      <Typography color="error">{t('errorLoadingData')}</Typography>
                    </Box>
                  ) : (
                  <> 
                  <Grid container justifyContent="flex-end" style={{ marginBottom: '3%' }}>
                    <Button variant="contained" color="primary" onClick={handleAddTransporter}>
                      <span style={{ fontSize: '1.2rem', marginRight: '5px' }}>+</span>
                      {t('newTransporter')}
                    </Button>
                  </Grid>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableBody>
                        {totalCount > 0 ? (
                          transportersState.map((transporter) => (
                            <TableRow key={transporter.id}>
                              <TableCell>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <span
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      backgroundColor: transporter.color,
                                      display: 'inline-block',
                                      marginRight: '8px',
                                      borderRadius: '2px',
                                    }}
                                  ></span>
                                  {transporter.name}
                                </div>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const primaryContact = transporter.transporterContacts?.find((c) => c.isPrimary)
                                  const displayName = primaryContact
                                    ? `${primaryContact.firstName} ${primaryContact.lastName}`
                                    : transporter.firstNameContact
                                      ? `${transporter.firstNameContact} ${transporter.lastNameContact}`
                                      : '-'
                                  const displayEmail = primaryContact?.email || transporter.email || '-'
                                  const displayPhone = primaryContact?.phoneNumber || transporter.phoneNumber || '-'
                                  return (
                                    <div>
                                      <div>{displayName}</div>
                                      <div>{displayEmail}</div>
                                      <div>{displayPhone}</div>
                                    </div>
                                  )
                                })()}
                              </TableCell>
                              <TableCell>
                                <Button onClick={() => handleEditClick(transporter)}>
                                  <img
                                    src="icons/picto-edit.svg"
                                    alt="edit Icon"
                                    style={{ width: '24px', height: '24px' }}
                                  />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <div style={{ textAlign: 'center', padding: '20px' }}>
                                {t('noTransporter')}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {/* Utilisation du composant CustomPagination */}
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                  </>
                  )}
                 
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </LayoutContainer>
      <TransporterModal
        open={isModalOpen}
        onClose={handleModalClose}
        mode={modalMode}
        refresh={handleRefreshList}
        initialData={currentTransporter}
      />
    </div>
  )
}

export default Transporters